import './irt-withdrawals-waiting.scss';
import * as React from 'react';
import { Empty, Modal, Table as AntdTable, Tag, Tooltip } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import {
    CheckOutlined,
    CloseOutlined,
    CopyOutlined,
    DollarCircleOutlined,
    ExportOutlined,
    InfoCircleOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import moment from 'jalali-moment';
import toast from 'react-hot-toast';
import { Card } from 'components/card/card';
import { UserLink, CopyablePhone } from 'components';
import { Button, buttonType } from 'components/button/button';
import { ColumnSearch } from 'components/column-search/column-search';
import { Page } from 'components/page/page';
import { PaginationList } from 'core/domain';
import {
    IrtWithdrawalAdminAction,
    Transaction,
    TransactionCount,
    TransactionFilterValues,
    TransactionMarketType,
    TransactionState,
    TransactionType,
} from 'core/domain/transaction/transaction';
import { SortDirection } from 'core/domain/order/order';
import { digitSperator, faNumToEnNum } from 'core/utils/decimalExtentions';
import { copyToClipboard } from 'core/utils/copyToClipboard';
import { downloadCSV } from 'core/utils/export-to-file';
import { jFormat } from 'core/utils/jFormat';
import { changeIrtWithdrawalState, getAllTransactions } from 'core/repositories/transaction';
import { TransactionDetailModal } from '../transactions/transactio-details/transaction-detail';

type Filters = Partial<TransactionFilterValues>;

const initialFilters: Filters = {
    sort: SortDirection.DESC,
};

// این صفحه فقط برداشت‌های ریالیِ دستیِ در انتظار تایید ادمین را نشان می‌دهد؛
// این چهار شرط ثابت‌اند و با فیلترهای کاربر قابل تغییر نیستند.
const FIXED_QUERY =
    `marketType=${TransactionMarketType.Currency}` +
    `&type=${TransactionType.Withdrawal}` +
    `&state=${TransactionState.WaitingForAdmin}` +
    `&isAutoGenerated=false`;

function buildQuery(filters: Filters): string {
    const parameters = new URLSearchParams(FIXED_QUERY);

    if (filters.trackingCode) parameters.set('trackingCode', filters.trackingCode.trim());
    if (filters.fullName) parameters.set('fullName', filters.fullName.trim());
    if (filters.phoneNumber) parameters.set('phoneNumber', faNumToEnNum(filters.phoneNumber.trim()));
    if (filters.bankAccount) parameters.set('bankAccount', faNumToEnNum(filters.bankAccount.trim()));
    if (filters.creatorId) parameters.set('creatorId', filters.creatorId.trim());
    if (filters.minAmount) parameters.set('minAmount', faNumToEnNum(filters.minAmount.trim()));
    if (filters.maxAmount) parameters.set('maxAmount', faNumToEnNum(filters.maxAmount.trim()));
    if (filters.startDate) parameters.set('startDate', jFormat(filters.startDate));
    if (filters.endDate) parameters.set('endDate', jFormat(filters.endDate));
    if (filters.sort !== undefined) parameters.set('sort', String(filters.sort));

    return `?${parameters.toString()}`;
}

const actionTexts: Record<IrtWithdrawalAdminAction, { title: string; content: (row: Transaction) => string }> = {
    [IrtWithdrawalAdminAction.Approve]: {
        title: 'تایید برداشت',
        content: (row) =>
            `آیا از تایید برداشت ${digitSperator(Math.abs(row.amount))} تومانی کاربر ${row.createdBy} اطمینان دارید؟ وضعیت تراکنش «انجام شده» می‌شود.`,
    },
    [IrtWithdrawalAdminAction.ApproveAndPayout]: {
        title: 'تایید و تسویه برداشت',
        content: (row) =>
            `آیا از تایید و تسویه برداشت ${digitSperator(Math.abs(row.amount))} تومانی کاربر ${row.createdBy} اطمینان دارید؟ مبلغ از طریق درگاه به شبا کاربر واریز می‌شود.`,
    },
    [IrtWithdrawalAdminAction.Reject]: {
        title: 'رد برداشت',
        content: (row) =>
            `آیا از رد برداشت ${digitSperator(Math.abs(row.amount))} تومانی کاربر ${row.createdBy} اطمینان دارید؟ وضعیت تراکنش «رد شده» می‌شود.`,
    },
};

export const IrtWithdrawalsWaitingForAdmin = () => {
    const [transactionsList, setTransactionsList] = React.useState<PaginationList<Transaction>>();
    const [transactionsCount, setTransactionsCount] = React.useState<TransactionCount>();
    const [cardLoading, setCardLoading] = React.useState(false);
    const [actionLoading, setActionLoading] = React.useState(false);
    const [isDetailOpen, setIsDetailOpen] = React.useState(false);
    const [selectedItem, setSelectedItem] = React.useState<Transaction>();
    const [pageSize, setPageSize] = React.useState(10);
    const [query, setQuery] = React.useState(buildQuery(initialFilters));
    const [filterValues, setFilterValues] = React.useState<Filters>(initialFilters);

    const fetchList = React.useCallback(
        (nextQuery: string = query, page: number = 1, size: number = pageSize) =>
            getAllTransactions(setCardLoading, setTransactionsList, page, size, nextQuery, setTransactionsCount),
        [pageSize, query],
    );

    const applyColumnSearch = React.useCallback(
        (patch: Record<string, any>) => {
            const nextFilters = { ...filterValues, ...patch } as Filters;
            const nextQuery = buildQuery(nextFilters);
            setFilterValues(nextFilters);
            setQuery(nextQuery);
            fetchList(nextQuery, 1);
        },
        [fetchList, filterValues],
    );

    React.useEffect(() => {
        const initialQuery = buildQuery(initialFilters);
        setQuery(initialQuery);
        getAllTransactions(setCardLoading, setTransactionsList, 1, 10, initialQuery, setTransactionsCount);
    }, []);

    const handleTableChange = React.useCallback(
        (
            pagination: TablePaginationConfig,
            _filters: unknown,
            sorter: SorterResult<Transaction> | SorterResult<Transaction>[],
        ) => {
            const nextPage = pagination.current ?? 1;
            const nextPageSize = pagination.pageSize ?? pageSize;
            const currentPage = transactionsCount?.pageIndex ?? 1;
            const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
            const nextSort =
                activeSorter?.columnKey === 'createdAt'
                    ? activeSorter.order === 'ascend'
                        ? SortDirection.ASC
                        : activeSorter.order === 'descend'
                          ? SortDirection.DESC
                          : undefined
                    : filterValues.sort;
            const sortChanged = nextSort !== filterValues.sort;
            const nextFilters = sortChanged ? { ...filterValues, sort: nextSort } : filterValues;
            const nextQuery = sortChanged ? buildQuery(nextFilters) : query;

            if (!sortChanged && nextPage === currentPage && nextPageSize === pageSize) return;

            setPageSize(nextPageSize);
            if (sortChanged) {
                setFilterValues(nextFilters);
                setQuery(nextQuery);
            }
            fetchList(nextQuery, sortChanged ? 1 : nextPage, nextPageSize);
        },
        [fetchList, filterValues, pageSize, query, transactionsCount?.pageIndex],
    );

    // هر سه عملیات برگشت‌ناپذیرند، پس قبل از ارسال درخواست یک تاییدیه‌ی صریح می‌گیریم.
    const confirmAction = React.useCallback(
        (row: Transaction, action: IrtWithdrawalAdminAction) => {
            const { title, content } = actionTexts[action];

            Modal.confirm({
                title,
                content: content(row),
                icon: <WarningOutlined />,
                okText: 'تایید',
                cancelText: 'انصراف',
                centered: true,
                okButtonProps: { danger: action === IrtWithdrawalAdminAction.Reject },
                onOk: () =>
                    changeIrtWithdrawalState(setActionLoading, row.id, action, () => {
                        toast.success('عملیات با موفقیت انجام شد');
                        // بعد از خروج ردیف از لیست، اگر صفحه‌ی فعلی خالی شود یک صفحه عقب می‌رویم.
                        const isLastItemOnPage = (transactionsList?.items?.length ?? 0) === 1;
                        const currentPage = transactionsCount?.pageIndex ?? 1;
                        fetchList(query, isLastItemOnPage && currentPage > 1 ? currentPage - 1 : currentPage);
                    }),
            });
        },
        [fetchList, query, transactionsCount?.pageIndex, transactionsList?.items?.length],
    );

    const columns = React.useMemo<ColumnsType<Transaction>>(
        () => [
            {
                title: '#',
                key: 'index',
                width: 70,
                render: (_, __, index) => ((transactionsCount?.pageIndex ?? 1) - 1) * pageSize + index + 1,
            },
            {
                title: (
                    <ColumnSearch
                        title="شناسه"
                        values={filterValues}
                        onApply={applyColumnSearch}
                        fields={[{ key: 'trackingCode', placeholder: 'شناسه تراکنش', dir: 'ltr' }]}
                    />
                ),
                dataIndex: 'trackingCode',
                key: 'trackingCode',
                width: 190,
                render: (trackingCode: string) => (
                    <span className="en copiable">
                        {trackingCode}
                        <CopyOutlined onClick={() => copyToClipboard(trackingCode, 'شناسه تراکنش')} />
                    </span>
                ),
            },
            {
                title: (
                    <ColumnSearch
                        title="کاربر"
                        values={filterValues}
                        onApply={applyColumnSearch}
                        fields={[
                            { key: 'fullName', placeholder: 'نام و نام خانوادگی' },
                            { key: 'phoneNumber', placeholder: 'شماره موبایل', dir: 'ltr' },
                            { key: 'creatorId', placeholder: 'شناسه کاربر', dir: 'ltr' },
                        ]}
                    />
                ),
                key: 'createdBy',
                width: 175,
                render: (_, row) => (
                    <div className="customer-cell">
                        <UserLink userId={row.userId}>
                            <span>{row.createdBy}</span>
                        </UserLink>
                        <small>
                            <CopyablePhone phoneNumber={row.phoneNumber} />
                        </small>
                    </div>
                ),
            },
            {
                title: (
                    <ColumnSearch
                        title="مبلغ"
                        values={filterValues}
                        onApply={applyColumnSearch}
                        fields={[
                            { key: 'minAmount', placeholder: 'حداقل مبلغ', dir: 'ltr', htmlType: 'number' },
                            { key: 'maxAmount', placeholder: 'حداکثر مبلغ', dir: 'ltr', htmlType: 'number' },
                        ]}
                    />
                ),
                dataIndex: 'amount',
                key: 'amount',
                align: 'left',
                width: 160,
                render: (amount: number, row) => (
                    <div className="amount-cell">
                        <span>{digitSperator(Math.abs(amount))}</span>
                        <small>تسویه: {digitSperator(row.payoutAmount)}</small>
                    </div>
                ),
            },
            {
                title: (
                    <ColumnSearch
                        title="حساب مقصد"
                        values={filterValues}
                        onApply={applyColumnSearch}
                        fields={[{ key: 'bankAccount', placeholder: 'شماره کارت / شبا', dir: 'ltr' }]}
                    />
                ),
                key: 'bankAccount',
                width: 230,
                render: (_, row) => (
                    <div className="bank-cell">
                        {row.bankAccount?.iBan && (
                            <span className="en copiable">
                                {row.bankAccount.iBan}
                                <CopyOutlined onClick={() => copyToClipboard(row.bankAccount.iBan, 'شماره شبا')} />
                            </span>
                        )}
                        {row.bankAccount?.cardNumber && (
                            <small className="en copiable">
                                {row.bankAccount.cardNumber}
                                <CopyOutlined
                                    onClick={() => copyToClipboard(row.bankAccount.cardNumber, 'شماره کارت')}
                                />
                            </small>
                        )}
                        {row.bankAccount?.bankName && <small>{row.bankAccount.bankName}</small>}
                    </div>
                ),
            },
            {
                title: 'پلتفرم',
                dataIndex: 'platform',
                key: 'platform',
                width: 120,
                render: (platform) => <Tag color={platform?.id === 1 ? 'blue' : 'green'}>{platform?.title}</Tag>,
            },
            {
                title: (
                    <ColumnSearch
                        title="زمان"
                        values={filterValues}
                        onApply={applyColumnSearch}
                        fields={[
                            { key: 'startDate', placeholder: 'از تاریخ', type: 'date' },
                            { key: 'endDate', placeholder: 'تا تاریخ', type: 'date' },
                        ]}
                    />
                ),
                dataIndex: 'createdAt',
                key: 'createdAt',
                width: 165,
                sorter: true,
                sortDirections: ['descend', 'ascend', 'descend'],
                sortOrder:
                    filterValues.sort === SortDirection.ASC
                        ? 'ascend'
                        : filterValues.sort === SortDirection.DESC
                          ? 'descend'
                          : undefined,
                render: (createdAt: string) => (
                    <span>
                        {moment.utc(createdAt).local().format('HH:mm')} {' - '}
                        {moment.utc(createdAt).locale('fa').local().format('jYYYY/MM/DD')}
                    </span>
                ),
            },
            {
                title: 'عملیات',
                key: 'actions',
                fixed: 'left',
                width: 150,
                render: (_, row) => (
                    <div className="table-actions">
                        <Tooltip title="تایید">
                            <button
                                type="button"
                                className="approve"
                                aria-label="تایید"
                                disabled={actionLoading}
                                onClick={() => confirmAction(row, IrtWithdrawalAdminAction.Approve)}
                            >
                                <CheckOutlined />
                            </button>
                        </Tooltip>
                        <Tooltip title="تایید و تسویه">
                            <button
                                type="button"
                                className="payout"
                                aria-label="تایید و تسویه"
                                disabled={actionLoading}
                                onClick={() => confirmAction(row, IrtWithdrawalAdminAction.ApproveAndPayout)}
                            >
                                <DollarCircleOutlined />
                            </button>
                        </Tooltip>
                        <Tooltip title="رد">
                            <button
                                type="button"
                                className="reject"
                                aria-label="رد"
                                disabled={actionLoading}
                                onClick={() => confirmAction(row, IrtWithdrawalAdminAction.Reject)}
                            >
                                <CloseOutlined />
                            </button>
                        </Tooltip>
                        <Tooltip title="جزئیات تراکنش">
                            <button
                                type="button"
                                aria-label="جزئیات تراکنش"
                                onClick={() => {
                                    setSelectedItem(row);
                                    setIsDetailOpen(true);
                                }}
                            >
                                <InfoCircleOutlined />
                            </button>
                        </Tooltip>
                    </div>
                ),
            },
        ],
        [actionLoading, applyColumnSearch, confirmAction, filterValues, pageSize, transactionsCount?.pageIndex],
    );

    return (
        <Page title="برداشت های از کیف پول ریالی در انتظار" className="pmx-irt-withdrawals-waiting">
            {/* اسپینر فقط روی خود جدول است؛ blur روی Card باعث دو اسپینر هم‌زمان می‌شد. */}
            <Card title="برداشت های از کیف پول ریالی در انتظار">
                {selectedItem && (
                    <TransactionDetailModal isOpen={isDetailOpen} setIsOpen={setIsDetailOpen} id={selectedItem.id} />
                )}
                <div className="table-toolbar">
                    <Button
                        className="export-btn"
                        text="خروجی اکسل"
                        type={buttonType.success}
                        icon={<ExportOutlined />}
                        onClick={() => downloadCSV('برداشت‌های ریالی در انتظار', transactionsList?.items ?? [])}
                    />
                </div>
                <AntdTable<Transaction>
                    className="irt-withdrawals-table"
                    rowKey="id"
                    columns={columns}
                    dataSource={transactionsList?.items ?? []}
                    loading={cardLoading || actionLoading}
                    locale={{ emptyText: <Empty description="برداشت ریالی در انتظار تاییدی وجود ندارد" /> }}
                    scroll={{ x: 1400 }}
                    pagination={{
                        current: transactionsCount?.pageIndex ?? 1,
                        pageSize: transactionsCount?.pageSize ?? pageSize,
                        total: transactionsCount?.totalCount ?? 0,
                        showSizeChanger: true,
                        pageSizeOptions: [10, 20, 50, 100, 1000, 10000, 100000],
                        showTotal: (total, range) =>
                            `نمایش ${digitSperator(range[0])} تا ${digitSperator(range[1])} از ${digitSperator(total)} رکورد`,
                    }}
                    onChange={handleTableChange}
                />
            </Card>
        </Page>
    );
};
